import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Megaphone, Plus, Copy, RefreshCw, DollarSign, Users, CheckCircle2,
    Pencil, Trash2, ExternalLink, Zap, KeyRound, QrCode, Download, Share2, Home, Trophy, Ban,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { AmbassadorRefereesTable } from './AmbassadorRefereesTable';
import { AmbassadorCupPanel } from '../../components/admin/AmbassadorCupPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', year: 'numeric',
        });
    } catch { return ''; }
};

/**
 * Ambassador Admin Panel — non-player promo codes.
 *
 * Each ambassador holds one unique code anyone can hand out. New player
 * signups that use the code count toward the ambassador; once they cross
 * the qualifying-load threshold, the ambassador's pending balance grows
 * by payout_per_signup. Admin then records cash/Venmo/CashApp payouts
 * (decrements pending balance, logs to audit trail).
 */
export const AmbassadorAdminPanel = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        name: '', code: '', email: '', phone: '',
        payout_per_signup: 0, qualifying_load: 0, commission_pct: 10,
        initial_password: '', notes: '',
    });
    const [editing, setEditing] = useState(null);
    const [payoutFor, setPayoutFor] = useState(null);
    const [payoutAmt, setPayoutAmt] = useState('');
    const [payoutMethod, setPayoutMethod] = useState('cashapp');
    const [payoutNotes, setPayoutNotes] = useState('');
    const [history, setHistory] = useState({ signups: [], payouts: [] });
    const [historyFor, setHistoryFor] = useState(null);
    const [topPlayersFor, setTopPlayersFor] = useState(null);
    const [topPlayers, setTopPlayers] = useState([]);
    const [topLoading, setTopLoading] = useState(false);
    const [qrFor, setQrFor] = useState(null);
    const [approving, setApproving] = useState(null);
    const [approveAmt, setApproveAmt] = useState('5');
    const [approveLoad, setApproveLoad] = useState('20');
    const [house, setHouse] = useState(null);
    const [houseSaving, setHouseSaving] = useState(false);
    const [revSummary, setRevSummary] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/ambassadors`);
            setRows(data.rows || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load ambassadors');
        }
        try {
            const { data: h } = await axios.get(`${API}/admin/house-ambassador`);
            setHouse(h);
        } catch (e) { /* non-fatal */ }
        try {
            const { data: rs } = await axios.get(`${API}/admin/ambassadors/revenue-summary`);
            setRevSummary(rs);
        } catch (e) { /* non-fatal */ }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const saveHouse = async () => {
        if (!house) return;
        setHouseSaving(true);
        try {
            const { data } = await axios.put(`${API}/admin/house-ambassador`, {
                enabled: !!house.enabled,
                ambassador_id: house.ambassador_id || null,
                commission_pct: Number(house.commission_pct) || 0,
            });
            setHouse(data);
            toast.success('House ambassador saved');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save');
        }
        setHouseSaving(false);
    };

    const create = async () => {
        if (!form.name.trim() || !form.code.trim()) {
            toast.error('Name and code are required.');
            return;
        }
        setCreating(true);
        try {
            const res = await axios.post(`${API}/admin/ambassadors`, form);
            const tempPw = res.data?.initial_password;
            toast.success(
                `Ambassador ${form.name} created — code: ${form.code.toUpperCase()}` +
                (tempPw ? ` · password: ${tempPw}` : ''),
                tempPw ? { duration: 10000 } : undefined,
            );
            setForm({ name: '', code: '', email: '', phone: '',
                payout_per_signup: 0, qualifying_load: 0, commission_pct: 10,
                initial_password: '', notes: '' });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not create ambassador');
        }
        setCreating(false);
    };

    const saveEdit = async () => {
        if (!editing) return;
        try {
            await axios.patch(`${API}/admin/ambassadors/${editing.id}`, {
                name: editing.name,
                email: editing.email,
                phone: editing.phone,
                payout_per_signup: Number(editing.payout_per_signup) || 0,
                qualifying_load: Number(editing.qualifying_load) || 0,
                commission_pct: Number(editing.commission_pct) || 0,
                notes: editing.notes,
                status: editing.status,
                exclude_from_contests: !!editing.exclude_from_contests,
            });
            toast.success('Saved');
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save');
        }
    };

    const deactivate = async (a) => {
        if (!window.confirm(`Deactivate ${a.name}? Their code stops working for new signups. History is preserved.`)) return;
        try {
            await axios.delete(`${API}/admin/ambassadors/${a.id}`);
            toast.success(`${a.name} deactivated`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not deactivate');
        }
    };

    const recordPayout = async () => {
        const amt = Number(payoutAmt);
        if (!payoutFor || !(amt > 0)) { toast.error('Enter a positive amount'); return; }
        try {
            const { data } = await axios.post(
                `${API}/admin/ambassadors/${payoutFor.id}/payout`,
                { amount: amt, method: payoutMethod, notes: payoutNotes },
            );
            toast.success(`Paid $${amt.toFixed(2)} to ${data.ambassador.name}`);
            setPayoutFor(null); setPayoutAmt(''); setPayoutNotes(''); setPayoutMethod('cashapp');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not record payout');
        }
    };

    const openHistory = async (a) => {
        setHistoryFor(a);
        try {
            const [s, p] = await Promise.all([
                axios.get(`${API}/admin/ambassadors/${a.id}/signups`),
                axios.get(`${API}/admin/ambassadors/${a.id}/payouts`),
            ]);
            setHistory({ signups: s.data.rows || [], payouts: p.data.rows || [] });
        } catch { setHistory({ signups: [], payouts: [] }); }
    };

    const openTopPlayers = async (a) => {
        setTopPlayersFor(a);
        setTopPlayers([]);
        setTopLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/ambassadors/${a.id}/top-players?limit=15`);
            setTopPlayers(data.rows || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load top players');
        }
        setTopLoading(false);
    };

    const approveApp = async () => {
        if (!approving) return;
        const amt = Number(approveAmt);
        const loadAmt = Number(approveLoad);
        if (!(amt >= 0) || !(loadAmt >= 0)) { toast.error('Enter valid amounts'); return; }
        try {
            await axios.post(`${API}/admin/ambassadors/${approving.id}/approve`, {
                payout_per_signup: amt,
                qualifying_load: loadAmt,
            });
            toast.success(`${approving.name} approved — code ${approving.code} is now live`);
            setApproving(null); setApproveAmt('5'); setApproveLoad('20');
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not approve');
        }
    };

    const rejectApp = async (a) => {
        if (!window.confirm(`Reject ${a.name}'s application? Their code ${a.code} will become available for someone else.`)) return;
        try {
            await axios.post(`${API}/admin/ambassadors/${a.id}/reject`, {});
            toast.success(`${a.name} rejected — code ${a.code} freed`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not reject');
        }
    };

    const copyShareLink = (code) => {
        const url = `${window.location.origin}/api/r/${code}`;
        navigator.clipboard?.writeText(url);
        toast.success(`Signup link copied — share it anywhere!`);
    };

    const shareQr = async (code) => {
        const qrUrl = `${API}/r/${code}/qr.png`;
        try {
            const blob = await fetch(qrUrl).then((r) => r.blob());
            const file = new File([blob], `betohalics-${code}.png`, { type: 'image/png' });
            if (navigator.share && navigator.canShare?.({ files: [file] })) {
                await navigator.share({
                    files: [file],
                    title: `Betohalics · code ${code}`,
                    text: `Scan to sign up with code ${code}`,
                });
                return;
            }
        } catch { /* fall through to opening the image */ }
        window.open(qrUrl, '_blank');
    };

    const [bundleBusy, setBundleBusy] = useState(false);
    const downloadAllQrs = async () => {
        setBundleBusy(true);
        const t = toast.loading('Building QR bundle…');
        try {
            const { data } = await axios.get(`${API}/admin/ambassadors/qr-bundle.zip`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'betohalics-ambassador-qrs.zip';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('QR bundle downloaded', { id: t });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not build QR bundle', { id: t });
        } finally {
            setBundleBusy(false);
        }
    };

    // Aggregate stats (active ambassadors only)
    const totals = useMemo(() => rows.filter(a => a.status !== 'pending_approval').reduce(
        (acc, a) => ({
            ambassadors: acc.ambassadors + (a.status === 'active' ? 1 : 0),
            signups: acc.signups + a.signup_count,
            qualified: acc.qualified + a.qualified_count,
            pending: acc.pending + a.pending_balance,
            paid: acc.paid + a.total_paid,
        }),
        { ambassadors: 0, signups: 0, qualified: 0, pending: 0, paid: 0 },
    ), [rows]);

    const pendingApps = useMemo(() => rows.filter(a => a.status === 'pending_approval'), [rows]);
    const liveRows = useMemo(() => rows.filter(a => a.status !== 'pending_approval'), [rows]);

    const handleResetPassword = async (amb) => {
        // Confirm + offer choice between auto-generate vs custom
        const custom = window.prompt(
            `Reset login password for ${amb.name} (${amb.email || amb.phone || 'no contact'})\n\n` +
            `• Type a custom password (min 6 chars) and click OK\n` +
            `• Leave blank and click OK to auto-generate a strong one\n` +
            `• Click Cancel to abort`,
            ''
        );
        if (custom === null) return; // user cancelled
        if (custom && custom.length > 0 && custom.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        try {
            const { data } = await axios.post(
                `${API}/admin/ambassadors/${amb.id}/set-password`,
                { password: custom || undefined },
                { withCredentials: true },
            );
            // Show the new password so admin can pass it on. Use prompt so it
            // can be copied even if toast hides too fast.
            const login = data.login_email || data.login_phone || '(no email/phone)';
            window.prompt(
                `Password set for ${amb.name}.\n\n` +
                `Login (use on the regular sign-in page):\n  ${login}\n\n` +
                (data.generated ? 'Auto-generated password — copy it now:' : 'New password:'),
                data.password
            );
            toast.success(`Password ${data.generated ? 'generated' : 'updated'} for ${amb.name}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not reset password');
        }
    };

    return (
        <div className="space-y-4" data-testid="ambassador-admin">
            <AmbassadorCupPanel />
            {/* Header + Create */}
            <div className="card-dark p-5" style={{ borderLeft: '3px solid #A855F7' }}>
                <div className="flex items-center gap-2 mb-1">
                    <Megaphone className="w-5 h-5 text-[#A855F7]" />
                    <h3 className="text-lg font-bold text-white">Ambassador Codes</h3>
                </div>
                <p className="text-xs text-[#A0AAB2] mb-4">
                    Non-player promo codes. Ambassadors earn a rev-share % of the net profit from the players they refer, credited to their pending balance.
                    Pay out via Cash App / Venmo / cash and record it here for the audit trail.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
                    <SummaryCard label="Active" value={totals.ambassadors} color="#FFFFFF" testId="amb-summary-active" />
                    <SummaryCard label="Total signups" value={totals.signups} color="#A0AAB2" testId="amb-summary-signups" />
                    <SummaryCard label="Qualified" value={totals.qualified} color="#39FF14" testId="amb-summary-qualified" />
                    <SummaryCard label="Pending $" value={`$${totals.pending.toFixed(2)}`} color="#FFD700" testId="amb-summary-pending" />
                    <SummaryCard label="Paid out" value={`$${totals.paid.toFixed(2)}`} color="#A855F7" testId="amb-summary-paid" />
                </div>

                {/* House vs real ambassadors — where the revenue comes from */}
                {revSummary && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5" data-testid="revenue-summary">
                        {[
                            { key: 'house', title: 'House (auto-assigned)', data: revSummary.house, accent: '#FFD700', icon: Home, filter: '__house__' },
                            { key: 'ambassadors', title: 'Ambassador links', data: revSummary.ambassadors, accent: '#A855F7', icon: Megaphone, filter: '__amb__' },
                        ].map(({ key, title, data, accent, icon: Icon, filter }) => (
                            <button key={key} type="button"
                                onClick={() => window.dispatchEvent(new CustomEvent('admin:goto-players', { detail: { filter } }))}
                                className="text-left rounded-lg bg-[#0a0a0a] border p-4 transition hover:brightness-125 cursor-pointer"
                                style={{ borderColor: `${accent}44` }} data-testid={`revenue-summary-${key}`}
                                title="View these players">
                                <div className="flex items-center gap-2 mb-3">
                                    <Icon className="w-4 h-4" style={{ color: accent }} />
                                    <p className="text-sm font-bold" style={{ color: accent }}>{title}</p>
                                    <span className="ml-auto text-[10px] text-[#A0AAB2]">View players →</span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center">
                                    <div>
                                        <p className="text-lg font-black text-white">{data.players}</p>
                                        <p className="text-[10px] uppercase tracking-wide text-[#A0AAB2]">Players</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-black text-[#cfd6db]">${(data.net_profit || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                        <p className="text-[10px] uppercase tracking-wide text-[#A0AAB2]">Net to house</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-black text-[#34d399]">${(data.commission || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                        <p className="text-[10px] uppercase tracking-wide text-[#A0AAB2]">Commission</p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                {/* House ambassador config — who unassigned players lock into */}
                {house && (
                    <div className="rounded-lg bg-[#0a0a0a] border border-[#FFD700]/30 p-4 mb-5" data-testid="house-ambassador-card">
                        <div className="flex items-center gap-2 mb-1">
                            <Home className="w-4 h-4 text-[#FFD700]" />
                            <p className="text-sm font-bold text-[#FFD700]">House Ambassador</p>
                            <span className="text-[10px] text-[#A0AAB2]">{house.assigned_count} players auto-assigned</span>
                        </div>
                        <p className="text-[11px] text-[#A0AAB2] mb-3">
                            Every player who signs up without an ambassador link/QR is automatically locked to this ambassador at the rate below.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                            <div className="sm:col-span-2">
                                <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Ambassador</Label>
                                <select
                                    value={house.ambassador_id || ''}
                                    onChange={(e) => setHouse({ ...house, ambassador_id: e.target.value })}
                                    className="w-full h-9 bg-[#121212] border border-white/10 rounded-md text-white text-sm px-2"
                                    data-testid="house-ambassador-select"
                                >
                                    <option value="">— Select ambassador —</option>
                                    {rows.map((a) => (
                                        <option key={a.id} value={a.id}>{a.name} ({a.code})</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Commission %</Label>
                                <Input type="number" min="0" max="100" step="1"
                                    value={house.commission_pct}
                                    onChange={(e) => setHouse({ ...house, commission_pct: e.target.value })}
                                    className="h-9 bg-[#121212] border-white/10 text-white text-sm"
                                    data-testid="house-commission-pct" />
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input type="checkbox" checked={!!house.enabled}
                                        onChange={(e) => setHouse({ ...house, enabled: e.target.checked })}
                                        className="w-4 h-4 accent-[#FFD700]"
                                        data-testid="house-enabled-toggle" />
                                    <span className="text-xs text-[#cfd6db]">Enabled</span>
                                </label>
                                <Button onClick={saveHouse} disabled={houseSaving}
                                    className="h-9 bg-[#FFD700] hover:bg-[#e6c200] text-black font-bold px-4"
                                    data-testid="house-save-btn">
                                    {houseSaving ? 'Saving…' : 'Save'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Inline create form */}
                <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-4">
                    <p className="text-[11px] text-[#A0AAB2] uppercase tracking-wider font-bold mb-2">Add ambassador</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Name</Label>
                            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm"
                                placeholder="Mike Smith" data-testid="amb-name" />
                        </div>
                        <div>
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Code</Label>
                            <Input value={form.code}
                                onChange={(e) => setForm({...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '')})}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm font-mono"
                                placeholder="MIKE5" maxLength={24} data-testid="amb-code" />
                        </div>
                        <div>
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Email</Label>
                            <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm"
                                placeholder="optional" data-testid="amb-email" />
                        </div>
                        <div>
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Phone</Label>
                            <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm"
                                placeholder="optional" data-testid="amb-phone" />
                        </div>
                        <div>
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Qualifying load</Label>
                            <Input type="number" min="0" step="1" value={form.qualifying_load}
                                onChange={(e) => setForm({...form, qualifying_load: e.target.value})}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm"
                                data-testid="amb-threshold" />
                        </div>
                        <div>
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Rev-share %</Label>
                            <Input type="number" min="0" max="100" step="0.5" value={form.commission_pct}
                                onChange={(e) => setForm({...form, commission_pct: e.target.value})}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm"
                                data-testid="amb-commission-pct" />
                        </div>
                        <div>
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Initial password</Label>
                            <Input value={form.initial_password}
                                onChange={(e) => setForm({...form, initial_password: e.target.value})}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm"
                                placeholder="leave blank to auto-generate"
                                data-testid="amb-initial-pw" />
                        </div>
                        <div className="sm:col-span-3">
                            <Label className="text-[10px] text-[#A0AAB2] mb-1 block">Notes (private)</Label>
                            <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}
                                className="h-9 bg-[#121212] border-white/10 text-white text-sm"
                                placeholder="@miketheinfluencer · paid via cashapp" data-testid="amb-notes" />
                        </div>
                        <div className="sm:col-span-2 flex items-end">
                            <Button onClick={create} disabled={creating}
                                className="w-full font-bold h-9"
                                style={{ background: 'linear-gradient(135deg, #A855F7, #FF4DB8)', color: 'white' }}
                                data-testid="amb-create-btn">
                                <Plus className="w-4 h-4 mr-1" /> {creating ? 'Adding…' : 'Add ambassador'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Pending applications (from public /ambassadors form) */}
            {pendingApps.length > 0 && (
                <div className="card-dark p-4" style={{ borderLeft: '3px solid #FFD700' }} data-testid="amb-pending-section">
                    <div className="flex items-center gap-2 mb-3">
                        <Megaphone className="w-4 h-4 text-[#FFD700]" />
                        <h4 className="text-sm font-bold text-white">Pending applications</h4>
                        <span className="text-[9px] font-black uppercase tracking-wider text-[#FFD700] bg-[#FFD700]/15 border border-[#FFD700]/40 px-2 py-0.5 rounded-full ml-1">
                            {pendingApps.length} waiting
                        </span>
                    </div>
                    <ul className="space-y-2">
                        {pendingApps.map((a) => (
                            <li key={a.id} className="rounded-lg bg-[#0a0a0a] border border-[#FFD700]/30 p-3" data-testid={`amb-pending-${a.id}`}>
                                <div className="flex flex-wrap items-start gap-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white font-bold">{a.name}
                                            <code className="text-[#A855F7] font-mono font-bold ml-2">{a.code}</code>
                                        </p>
                                        <p className="text-[10px] text-[#A0AAB2]">
                                            {a.email || '—'} {a.phone && `· ${a.phone}`} · applied {fmtCT(a.applied_at)}
                                        </p>
                                        {a.pitch && (
                                            <p className="text-[11px] text-[#E0E0E0] mt-2 italic bg-[#0e0e0e] rounded p-2 border-l-2 border-[#A855F7]/40">
                                                "{a.pitch}"
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <Button
                                            onClick={() => { setApproving(a); setApproveAmt('5'); setApproveLoad('20'); }}
                                            className="h-8 text-[11px] font-bold"
                                            style={{ background: 'linear-gradient(135deg, #39FF14, #008f0b)', color: 'black' }}
                                            data-testid={`amb-approve-${a.id}`}
                                        >
                                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                                        </Button>
                                        <Button
                                            onClick={() => rejectApp(a)}
                                            variant="outline"
                                            className="h-8 text-[11px] border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                                            data-testid={`amb-reject-${a.id}`}
                                        >
                                            <Trash2 className="w-3 h-3 mr-1" /> Reject
                                        </Button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* List */}
            <div className="card-dark p-4">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-white">All ambassadors</h4>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={downloadAllQrs} disabled={bundleBusy || liveRows.length === 0}
                            className="h-8 px-3 rounded text-[11px] font-bold border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                            data-testid="amb-qr-bundle-btn" title="Download every ambassador's QR as a ZIP">
                            <QrCode className="w-3.5 h-3.5" /> {bundleBusy ? 'Building…' : 'Download all QRs'}
                        </button>
                        <RunCronButton onDone={load} />
                        <button type="button" onClick={load} className="text-[#A0AAB2] hover:text-white" data-testid="amb-refresh">
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
                {liveRows.length === 0 ? (
                    <p className="text-center text-xs text-[#A0AAB2] py-8" data-testid="amb-empty">No ambassadors yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-[#A0AAB2] border-b border-white/10">
                                    <th className="p-2 font-bold">Ambassador</th>
                                    <th className="p-2 font-bold">Code</th>
                                    <th className="p-2 font-bold text-right">Rev-share %</th>
                                    <th className="p-2 font-bold text-right">Signups</th>
                                    <th className="p-2 font-bold text-right">Qualified</th>
                                    <th className="p-2 font-bold text-right">Pending $</th>
                                    <th className="p-2 font-bold text-right">Paid out</th>
                                    <th className="p-2 font-bold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {liveRows.map((a) => (
                                    <tr key={a.id} className={`border-b border-white/5 ${a.status === 'inactive' ? 'opacity-50' : ''}`} data-testid={`amb-row-${a.id}`}>
                                        <td className="p-2">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <p className="text-white font-bold">{a.name}</p>
                                                {a.exclude_from_contests && (
                                                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30"
                                                        title="Hidden from the Ambassador Cup & public leaderboard"
                                                        data-testid={`amb-excluded-badge-${a.id}`}>
                                                        <Ban className="w-2.5 h-2.5" /> Excluded from Cup
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-[#A0AAB2]">{a.email || a.phone || '—'}</p>
                                        </td>
                                        <td className="p-2">
                                            <div className="flex items-center gap-1">
                                                <code className="text-[#A855F7] font-mono font-bold">{a.code}</code>
                                                <button type="button" onClick={() => copyShareLink(a.code)}
                                                    className="text-[#A0AAB2] hover:text-white" title="Copy signup link"
                                                    data-testid={`amb-copy-${a.id}`}>
                                                    <Copy className="w-3 h-3" />
                                                </button>
                                            </div>
                                            {a.status === 'inactive' && (
                                                <span className="text-[9px] text-[#FF3B30] font-bold uppercase">Inactive</span>
                                            )}
                                        </td>
                                        <td className="p-2 text-right">
                                            <CommissionPctCell ambassador={a} onSaved={load} />
                                        </td>
                                        <td className="p-2 text-right text-white">{a.signup_count}</td>
                                        <td className="p-2 text-right text-[#39FF14] font-bold">{a.qualified_count}</td>
                                        <td className="p-2 text-right font-mono font-black text-[#FFD700]">${a.pending_balance.toFixed(2)}</td>
                                        <td className="p-2 text-right font-mono text-[#A855F7]">${a.total_paid.toFixed(2)}</td>
                                        <td className="p-2">
                                            <div className="flex items-center justify-end gap-1">
                                                <button type="button" onClick={() => setQrFor(a)}
                                                    className="h-7 px-2 rounded text-[10px] text-[#39FF14] hover:bg-[#39FF14]/10"
                                                    title="View / send QR code" data-testid={`amb-qr-${a.id}`}>
                                                    <QrCode className="w-3 h-3" />
                                                </button>
                                                <button type="button" onClick={() => { setPayoutFor(a); setPayoutAmt(a.pending_balance.toFixed(2)); }}
                                                    disabled={a.pending_balance <= 0}
                                                    className="h-7 px-2 rounded text-[10px] font-bold border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 disabled:opacity-30 disabled:cursor-not-allowed"
                                                    data-testid={`amb-payout-${a.id}`}>
                                                    <DollarSign className="w-3 h-3 inline" /> Pay
                                                </button>
                                                <button type="button" onClick={() => openHistory(a)}
                                                    className="h-7 px-2 rounded text-[10px] text-[#A0AAB2] hover:text-white"
                                                    title="History" data-testid={`amb-history-${a.id}`}>
                                                    <ExternalLink className="w-3 h-3" />
                                                </button>
                                                <button type="button" onClick={() => openTopPlayers(a)}
                                                    className="h-7 px-2 rounded text-[10px] text-[#FFD700] hover:text-white"
                                                    title="Top players by profit" data-testid={`amb-top-players-${a.id}`}>
                                                    <Trophy className="w-3 h-3" />
                                                </button>
                                                <button type="button" onClick={() => setEditing({...a})}
                                                    className="h-7 px-2 rounded text-[10px] text-[#A0AAB2] hover:text-white"
                                                    title="Edit" data-testid={`amb-edit-${a.id}`}>
                                                    <Pencil className="w-3 h-3" />
                                                </button>
                                                <button type="button" onClick={() => handleResetPassword(a)}
                                                    className="h-7 px-2 rounded text-[10px] text-[#FFD700] hover:bg-[#FFD700]/10"
                                                    title="Reset / set login password" data-testid={`amb-pw-${a.id}`}>
                                                    <KeyRound className="w-3 h-3" />
                                                </button>
                                                <button type="button" onClick={() => deactivate(a)}
                                                    disabled={a.status === 'inactive'}
                                                    className="h-7 px-2 rounded text-[10px] text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-30"
                                                    title="Deactivate" data-testid={`amb-delete-${a.id}`}>
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* QR code dialog */}
            <Dialog open={!!qrFor} onOpenChange={(o) => !o && setQrFor(null)}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-sm" data-testid="amb-qr-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <QrCode className="w-4 h-4 text-[#39FF14]" /> {qrFor?.name}'s QR
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Scans to <span className="text-white font-mono">/register?ref={qrFor?.code}</span> — send this to the ambassador for their marketing materials.
                        </DialogDescription>
                    </DialogHeader>
                    {qrFor && (
                        <div className="flex flex-col items-center gap-3 py-1">
                            <div className="rounded-lg bg-white p-2">
                                <img src={`${API}/r/${qrFor.code}/qr.png`} alt={`QR for code ${qrFor.code}`}
                                    className="w-52 h-auto" data-testid="amb-qr-img" />
                            </div>
                            <code className="text-[#A855F7] font-mono font-bold text-sm">{qrFor.code}</code>
                            {/* The actual share link, visible + copyable so admins can
                                send it straight to customers (not just the QR). */}
                            <div className="w-full">
                                <Label className="text-[10px] uppercase text-[#A0AAB2] mb-1 block">Share link</Label>
                                <div className="flex gap-2">
                                    <Input readOnly value={`${window.location.origin}/api/r/${qrFor.code}`}
                                        onFocus={(e) => e.target.select()}
                                        className="h-9 bg-[#121212] border-white/10 text-white text-[11px] font-mono"
                                        data-testid="amb-qr-share-link" />
                                    <Button type="button" onClick={() => copyShareLink(qrFor.code)}
                                        className="h-9 bg-[#FFD700] hover:bg-[#e6c200] text-black font-bold px-3"
                                        data-testid="amb-qr-copy-link">
                                        <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                                    </Button>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 w-full">
                                <a href={`${API}/r/${qrFor.code}/qr.png`} download={`betohalics-${qrFor.code}.png`}
                                    className="flex-1 h-9 rounded flex items-center justify-center gap-1.5 text-xs font-bold border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10"
                                    data-testid="amb-qr-download">
                                    <Download className="w-3.5 h-3.5" /> Download
                                </a>
                                <button type="button" onClick={() => shareQr(qrFor.code)}
                                    className="flex-1 h-9 rounded flex items-center justify-center gap-1.5 text-xs font-bold border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10"
                                    data-testid="amb-qr-share">
                                    <Share2 className="w-3.5 h-3.5" /> Share
                                </button>
                            </div>
                            <a href={`${API}/r/${qrFor.code}/flyer.pdf`} target="_blank" rel="noreferrer"
                                className="text-[11px] text-[#A0AAB2] hover:text-white underline" data-testid="amb-qr-flyer">
                                Download printable flyer (PDF)
                            </a>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Payout dialog */}
            <Dialog open={!!payoutFor} onOpenChange={(v) => !v && setPayoutFor(null)}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-md" data-testid="amb-payout-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-[#FFD700]" />
                            Record payout to {payoutFor?.name}
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Pending balance: <span className="text-[#FFD700] font-bold">${payoutFor?.pending_balance?.toFixed(2)}</span>.
                            Use this AFTER you've actually paid them.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2]">Amount paid</Label>
                            <Input type="number" min="0.01" step="0.01" value={payoutAmt}
                                onChange={(e) => setPayoutAmt(e.target.value)}
                                className="bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="amb-payout-amount" />
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2]">Method</Label>
                            <select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)}
                                className="w-full h-9 rounded-md bg-[#0a0a0a] border border-white/10 text-white text-sm px-3"
                                data-testid="amb-payout-method">
                                <option value="cashapp">Cash App</option>
                                <option value="venmo">Venmo</option>
                                <option value="zelle">Zelle</option>
                                <option value="paypal">PayPal</option>
                                <option value="cash">Cash</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2]">Notes (e.g., $cashtag or txn id)</Label>
                            <Input value={payoutNotes} onChange={(e) => setPayoutNotes(e.target.value)}
                                className="bg-[#0a0a0a] border-white/10 text-white"
                                placeholder="$miketag" data-testid="amb-payout-notes" />
                        </div>
                    </div>
                    <DialogFooter className="flex-row gap-2">
                        <Button variant="outline" onClick={() => setPayoutFor(null)}
                            className="flex-1 border-white/10 text-white">Cancel</Button>
                        <Button onClick={recordPayout}
                            className="flex-1 font-bold"
                            style={{ background: 'linear-gradient(135deg, #FFD700, #FF8C00)', color: 'black' }}
                            data-testid="amb-payout-confirm">
                            Record payout
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-md" data-testid="amb-edit-dialog">
                    <DialogHeader><DialogTitle>Edit ambassador</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            <div><Label className="text-[11px] text-[#A0AAB2]">Name</Label>
                                <Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})}
                                    className="bg-[#0a0a0a] border-white/10 text-white" /></div>
                            <div><Label className="text-[11px] text-[#A0AAB2]">Email</Label>
                                <Input value={editing.email || ''} onChange={(e) => setEditing({...editing, email: e.target.value})}
                                    className="bg-[#0a0a0a] border-white/10 text-white" /></div>
                            <div><Label className="text-[11px] text-[#A0AAB2]">Phone</Label>
                                <Input value={editing.phone || ''} onChange={(e) => setEditing({...editing, phone: e.target.value})}
                                    className="bg-[#0a0a0a] border-white/10 text-white" /></div>
                            <div className="grid grid-cols-2 gap-2">
                                <div><Label className="text-[11px] text-[#A0AAB2]">Qualifying load</Label>
                                    <Input type="number" value={editing.qualifying_load}
                                        onChange={(e) => setEditing({...editing, qualifying_load: e.target.value})}
                                        className="bg-[#0a0a0a] border-white/10 text-white" /></div>
                                <div><Label className="text-[11px] text-[#A0AAB2]">Rev-share %</Label>
                                    <Input type="number" step="0.5" min="0" max="100"
                                        value={editing.commission_pct ?? 10}
                                        onChange={(e) => setEditing({...editing, commission_pct: e.target.value})}
                                        className="bg-[#0a0a0a] border-white/10 text-white"
                                        data-testid="amb-edit-commission-pct" /></div>
                            </div>
                            <div><Label className="text-[11px] text-[#A0AAB2]">Status</Label>
                                <select value={editing.status} onChange={(e) => setEditing({...editing, status: e.target.value})}
                                    className="w-full h-9 rounded-md bg-[#0a0a0a] border border-white/10 text-white text-sm px-3">
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select></div>
                            <div><Label className="text-[11px] text-[#A0AAB2]">Notes</Label>
                                <Input value={editing.notes || ''} onChange={(e) => setEditing({...editing, notes: e.target.value})}
                                    className="bg-[#0a0a0a] border-white/10 text-white" /></div>
                            <label className="flex items-start gap-3 rounded-md border border-white/10 bg-[#0a0a0a] p-3 cursor-pointer"
                                data-testid="amb-edit-exclude-contests-label">
                                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#A855F7]"
                                    checked={!!editing.exclude_from_contests}
                                    onChange={(e) => setEditing({...editing, exclude_from_contests: e.target.checked})}
                                    data-testid="amb-edit-exclude-contests" />
                                <span className="text-xs text-white">Exclude from contests
                                    <span className="block text-[10px] text-[#A0AAB2]">Keeps this ambassador out of the Ambassador Cup and public referrer leaderboard. Use for the House account.</span>
                                </span>
                            </label>
                        </div>
                    )}
                    <DialogFooter className="flex-row gap-2">
                        <Button variant="outline" onClick={() => setEditing(null)}
                            className="flex-1 border-white/10 text-white">Cancel</Button>
                        <Button onClick={saveEdit} className="flex-1 font-bold bg-[#A855F7] text-white"
                            data-testid="amb-edit-save">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Approve application dialog */}
            <Dialog open={!!approving} onOpenChange={(v) => !v && setApproving(null)}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-md" data-testid="amb-approve-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-[#39FF14]" />
                            Approve {approving?.name}
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Lock in the payout rate. Code <code className="text-[#A855F7] font-mono font-bold">{approving?.code}</code> goes live the moment you confirm.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 gap-3">
                        <div>
                            <Label className="text-[11px] text-[#A0AAB2]">Qualifying load ($)</Label>
                            <Input
                                type="number" min="0" step="1"
                                value={approveLoad}
                                onChange={(e) => setApproveLoad(e.target.value)}
                                className="bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="amb-approve-load"
                            />
                        </div>
                    </div>
                    <DialogFooter className="flex-row gap-2">
                        <Button variant="outline" onClick={() => setApproving(null)} className="flex-1 border-white/10 text-white">Cancel</Button>
                        <Button onClick={approveApp} className="flex-1 font-bold"
                            style={{ background: 'linear-gradient(135deg, #39FF14, #008f0b)', color: 'black' }}
                            data-testid="amb-approve-confirm">
                            Approve & activate
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Top players by profit dialog */}
            <Dialog open={!!topPlayersFor} onOpenChange={(v) => !v && setTopPlayersFor(null)}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid="amb-top-players-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-[#FFD700]" /> Top players — {topPlayersFor?.name}
                        </DialogTitle>
                        <DialogDescription>
                            Ranked by net profit they've driven for the house (code <code className="text-[#A855F7] font-mono">{topPlayersFor?.code}</code>)
                        </DialogDescription>
                    </DialogHeader>
                    {topLoading ? (
                        <p className="text-xs text-[#A0AAB2] py-6 text-center">Loading…</p>
                    ) : topPlayers.length === 0 ? (
                        <p className="text-xs text-[#666] py-6 text-center">No referred players yet.</p>
                    ) : (
                        <ul className="space-y-1">
                            {topPlayers.map((p, i) => (
                                <li key={p.player_id || i} className="flex items-center gap-3 bg-[#0a0a0a] rounded p-2.5"
                                    data-testid={`top-player-row-${i}`}>
                                    <span className="w-6 text-center font-black text-sm"
                                        style={{ color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#5f6b73' }}>
                                        {i + 1}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-white font-semibold truncate">{p.player_name}</p>
                                        <p className="text-[10px] text-[#A0AAB2]">Earned ${(p.commission || 0).toFixed(2)} · {p.commission_pct || 0}%</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-mono font-bold" style={{ color: p.net_house_profit >= 0 ? '#34d399' : '#f87171' }}>
                                            ${(p.net_house_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </p>
                                        <p className="text-[10px] text-[#A0AAB2]">net to house</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </DialogContent>
            </Dialog>


            {/* History dialog */}
            <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
                <DialogContent className="bg-[#0e0e0e] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>History — {historyFor?.name}</DialogTitle>
                        <DialogDescription>Signups + payouts for code <code className="text-[#A855F7] font-mono">{historyFor?.code}</code></DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <p className="text-[11px] uppercase tracking-wider text-[#A0AAB2] font-bold mb-2">Signups ({history.signups.length})</p>
                            {history.signups.length === 0 ? <p className="text-xs text-[#666]">None yet.</p> :
                                <ul className="space-y-1">
                                    {history.signups.map(s => (
                                        <li key={s.id} className="text-xs flex items-center gap-2 bg-[#0a0a0a] rounded p-2">
                                            <Users className="w-3 h-3 text-[#A0AAB2]" />
                                            <span className="text-white font-bold">{s.referee_name}</span>
                                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${s.status === 'qualified' ? 'bg-[#39FF14]/20 text-[#39FF14]' : 'bg-[#A0AAB2]/20 text-[#A0AAB2]'}`}>{s.status}</span>
                                            <span className="text-[10px] text-[#A0AAB2] ml-auto">
                                                ${s.loaded_progress.toFixed(2)} / ${s.qualifying_load.toFixed(2)}
                                            </span>
                                            <span className="text-[10px] text-[#666]">{fmtCT(s.created_at)}</span>
                                        </li>
                                    ))}
                                </ul>}
                        </div>
                        <div>
                            <p className="text-[11px] uppercase tracking-wider text-[#A0AAB2] font-bold mb-2">Payouts ({history.payouts.length})</p>
                            {history.payouts.length === 0 ? <p className="text-xs text-[#666]">None recorded.</p> :
                                <ul className="space-y-1">
                                    {history.payouts.map(p => (
                                        <li key={p.id} className="text-xs flex items-center gap-2 bg-[#0a0a0a] rounded p-2">
                                            <DollarSign className="w-3 h-3 text-[#FFD700]" />
                                            <span className="font-mono font-bold text-[#FFD700]">${p.amount.toFixed(2)}</span>
                                            <span className="text-[10px] uppercase font-bold text-[#A855F7]">{p.method}</span>
                                            {p.notes && <span className="text-[10px] text-[#A0AAB2]">· {p.notes}</span>}
                                            <span className="text-[10px] text-[#666] ml-auto">by {p.admin_email}</span>
                                            <span className="text-[10px] text-[#666]">{fmtCT(p.created_at)}</span>
                                        </li>
                                    ))}
                                </ul>}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Flat cross-ambassador referee table — all players from all ambassadors */}
            <AmbassadorRefereesTable />
        </div>
    );
};

const SummaryCard = ({ label, value, color, testId }) => (
    <div className="rounded-lg bg-[#0a0a0a] border border-white/10 p-3" data-testid={testId}>
        <p className="text-[10px] text-[#A0AAB2] uppercase tracking-wider font-bold">{label}</p>
        <p className="text-xl font-black tabular-nums mt-1" style={{ color }}>{value}</p>
    </div>
);

export default AmbassadorAdminPanel;

/**
 * "Run weekly commission cron now" button — fires the same accrual that
 * runs at 00:05 CT every Monday. Idempotent via `commission_paid_through`
 * high-water mark — clicking it as many times as you want will never
 * double-pay anyone.
 *
 * Companion "Preview" button shows the exact pending amount per ambassador
 * before you commit.
 */
const RunCronButton = ({ onDone }) => {
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [showPanel, setShowPanel] = useState(false);
    const [lastRun, setLastRun] = useState(null);

    const loadPreview = async () => {
        setPreviewBusy(true);
        try {
            const { data } = await axios.get(`${API}/admin/ambassadors/commission-preview`, { withCredentials: true });
            setPreview(data);
            setShowPanel(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load preview');
        } finally { setPreviewBusy(false); }
    };

    const run = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/ambassadors/run-daily-commissions`,
                {},
                { withCredentials: true },
            );
            setLastRun(data);
            const perAmb = data.per_ambassador || [];
            const msg = data.credited_count > 0
                ? `Credited ${data.credited_count} affiliate${data.credited_count === 1 ? '' : 's'} — total $${Number(data.credited_total).toFixed(2)}`
                : `Processed ${data.processed} signup${data.processed === 1 ? '' : 's'} — no new house profit since last run (nothing to credit)`;
            toast.success(msg, { duration: 8000 });
            // Refresh preview so admin sees the new $0 pending state
            try {
                const { data: p } = await axios.get(`${API}/admin/ambassadors/commission-preview`, { withCredentials: true });
                setPreview(p);
                setShowPanel(true);
            } catch { /* ignore */ }
            await onDone?.();
            // Force-log per-ambassador breakdown into the toast area
            perAmb.forEach((a) => {
                toast.success(`${a.name || a.code} · +$${Number(a.total).toFixed(2)} (${a.count} player${a.count === 1 ? '' : 's'})`, { duration: 6000 });
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Cron run failed');
        }
        setBusy(false);
    };

    return (
        <div className="flex flex-col gap-2 w-full" data-testid="amb-run-cron-panel">
            <div className="flex gap-2 items-center">
                <button type="button"
                    onClick={loadPreview}
                    disabled={previewBusy || busy}
                    className="h-7 px-2.5 rounded text-[10px] font-bold border border-[#00E1FF]/40 text-[#00E1FF] hover:bg-[#00E1FF]/10 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    title="See what would be credited right now — no changes to balances"
                    data-testid="amb-preview-btn">
                    <Zap className={`w-3 h-3 ${previewBusy ? 'animate-pulse' : ''}`} />
                    {previewBusy ? 'Loading…' : 'Preview pending'}
                </button>
                <button type="button"
                    onClick={run}
                    disabled={busy}
                    className="h-7 px-2.5 rounded text-[10px] font-bold border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    title="Post pending commissions to ambassador balances now (idempotent — safe to click repeatedly)"
                    data-testid="amb-run-cron">
                    <Zap className={`w-3 h-3 ${busy ? 'animate-pulse' : ''}`} />
                    {busy ? 'Posting…' : 'Post commissions now'}
                </button>
                {(preview || lastRun) && (
                    <button type="button"
                        onClick={() => setShowPanel((v) => !v)}
                        className="h-7 px-2.5 rounded text-[10px] font-bold border border-white/20 text-white/80 hover:bg-white/5"
                    >{showPanel ? 'Hide details' : 'Show details'}</button>
                )}
            </div>
            {showPanel && preview && (
                <div className="rounded border border-[#00E1FF]/30 bg-black/40 p-2" data-testid="amb-preview-panel">
                    <div className="text-[10px] uppercase tracking-widest text-[#00E1FF] font-black flex items-center justify-between">
                        <span>Real-time pending · {preview.ambassador_count} ambassador{preview.ambassador_count === 1 ? '' : 's'}</span>
                        <b className="text-[#39FF14] font-mono text-xs">Total owed: ${Number(preview.total_pending).toFixed(2)}</b>
                    </div>
                    {preview.per_ambassador.length === 0 ? (
                        <div className="text-[11px] text-white/50 italic mt-1">No commissionable activity since last run.</div>
                    ) : (
                        <div className="mt-1.5 max-h-56 overflow-y-auto">
                            <table className="w-full text-[11px]">
                                <thead className="text-[9px] uppercase text-white/50">
                                    <tr><th className="text-left py-1">Ambassador</th><th className="text-right">Players</th><th className="text-right">Net profit</th><th className="text-right">Owed now</th><th className="text-right">Already pending</th></tr>
                                </thead>
                                <tbody>
                                    {preview.per_ambassador.map((a) => (
                                        <tr key={a.ambassador_id} className="border-t border-white/5">
                                            <td className="py-1 font-semibold text-white">{a.name || '—'} <span className="text-white/40">({a.code || '?'})</span></td>
                                            <td className="text-right font-mono">{a.player_count}</td>
                                            <td className="text-right font-mono text-white/80">${a.lifetime_net_house_profit.toFixed(2)}</td>
                                            <td className="text-right font-mono font-black text-[#39FF14]">${a.pending_commission.toFixed(2)}</td>
                                            <td className="text-right font-mono text-[#FFD700]">${a.pending_balance.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="text-[9px] text-white/40 mt-1 italic">
                        Preview computed {new Date(preview.computed_at).toLocaleTimeString()}. Hit &ldquo;Post commissions now&rdquo; to credit these amounts to pending balances.
                    </div>
                </div>
            )}
            {showPanel && lastRun && lastRun.per_ambassador && lastRun.per_ambassador.length > 0 && (
                <div className="rounded border border-[#39FF14]/30 bg-black/40 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-[#39FF14] font-black">Last run · credited ${Number(lastRun.credited_total).toFixed(2)}</div>
                    <ul className="mt-1 text-[11px] space-y-0.5">
                        {lastRun.per_ambassador.map((a) => (
                            <li key={a.ambassador_id} className="flex justify-between">
                                <span className="text-white">{a.name || '?'} <span className="text-white/40">({a.code || '?'})</span></span>
                                <b className="font-mono text-[#39FF14]">+${Number(a.total).toFixed(2)}</b>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

/**
 * Inline-editable commission-percentage cell. Click the badge to edit, press
 * Enter or blur to save. Reverts on Escape.
 */
const CommissionPctCell = ({ ambassador, onSaved }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(String(ambassador.commission_pct ?? 10));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setValue(String(ambassador.commission_pct ?? 10));
    }, [ambassador.commission_pct]);

    const save = async () => {
        const num = Number(value);
        if (!Number.isFinite(num) || num < 0 || num > 100) {
            toast.error('Enter a number between 0 and 100');
            setValue(String(ambassador.commission_pct ?? 10));
            setEditing(false);
            return;
        }
        if (num === Number(ambassador.commission_pct)) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await axios.patch(`${API}/admin/ambassadors/${ambassador.id}`, { commission_pct: num });
            toast.success(`${ambassador.code}: rev-share set to ${num}%`);
            await onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
            setValue(String(ambassador.commission_pct ?? 10));
        }
        setSaving(false);
        setEditing(false);
    };

    if (editing) {
        return (
            <input
                type="number" min="0" max="100" step="0.5"
                autoFocus
                value={value}
                disabled={saving}
                onChange={(e) => setValue(e.target.value)}
                onBlur={save}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') save();
                    if (e.key === 'Escape') {
                        setValue(String(ambassador.commission_pct ?? 10));
                        setEditing(false);
                    }
                }}
                className="w-16 h-7 rounded bg-[#121212] border border-[#FFD700]/60 text-[#FFD700] text-sm text-right font-mono px-2 focus:outline-none focus:ring-1 focus:ring-[#FFD700]"
                data-testid={`amb-pct-input-${ambassador.id}`}
            />
        );
    }

    return (
        <button type="button" onClick={() => setEditing(true)}
            className="px-2 py-0.5 rounded text-xs font-mono font-bold text-[#FFD700] hover:bg-[#FFD700]/10 transition-colors"
            title="Click to edit rev-share %"
            data-testid={`amb-pct-cell-${ambassador.id}`}>
            {Number(ambassador.commission_pct ?? 10).toFixed(ambassador.commission_pct % 1 === 0 ? 0 : 1)}%
        </button>
    );
};
